import { Button, Color, Spacing } from '@billy/design-system'
import styled from '@emotion/styled'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { reactClass } from '../../utils'
import { LazyScrolling } from '../LazyScrolling'
import { Text } from '../Typography/Text'
import { SelectListItem } from './SelectListItem'
import * as Styled from './styles'
import { SmartSelectItem } from './types'

const FooterAction = styled.footer`
  border-top: 1px solid ${Color.Grey150};
  padding-top: ${Spacing.S8};
`

type SelectListProps<T> = {
  allowCreateNew?: boolean
  createNewText?: string
  disabled?: boolean
  isOpen?: boolean
  items: T[]
  height?: number | string
  maxHeight?: number | string
  notFoundText?: string
  onCreateNew?: () => void
  onItemSelect: (id: string) => void
  render?: (props: T) => React.ReactNode
  request?: () => void
  selectedId?: string
}

const renderListItem = <T extends SmartSelectItem>({ id, ...rest }: T) => (
  <SelectListItem id={id} key={id} data-list-item-id={id} {...rest} />
)

export const SelectList = <T extends SmartSelectItem>({
  allowCreateNew,
  createNewText,
  isOpen,
  items,
  maxHeight,
  notFoundText,
  onCreateNew = () => null,
  onItemSelect = () => null,
  render,
  request,
  selectedId,
  ...rest
}: SelectListProps<T>) => {
  const { t } = useTranslation()
  const handleCreateNew = () => onCreateNew()
  const handleSelect = (id: string) => onItemSelect(id)

  const createNewTextLine = createNewText
    ? t('components.smartselect.createnewitem', { name: createNewText })
    : t('components.smartselect.createnew')

  return (
    <Styled.SelecttListWrapper className={reactClass('smart-select-list')} {...rest}>
      {items?.length === 0 && notFoundText && <Text colorVariant="secondary">{notFoundText}</Text>}
      <LazyScrolling<T>
        items={items}
        maxHeight={maxHeight}
        onItemSelect={handleSelect}
        render={render || renderListItem}
        request={request}
        selectedId={selectedId}
        withFooter={allowCreateNew}
        withOverflowScroll
      />
      {allowCreateNew && (
        <FooterAction>
          <Button icon="circlePlus" variant="text" onClick={handleCreateNew} size="lg" fullWidth>
            {createNewTextLine}
          </Button>
        </FooterAction>
      )}
    </Styled.SelecttListWrapper>
  )
}
